import { useEffect, useState } from "react";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import SupplierProducts from "../Supplier/SupplierProducts";
import axios from "axios";
import CryptoJS from "crypto-js";
import toast from "react-hot-toast";

// Role → themeable tint
const ROLE_TINT = {
  buyer: { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
  supplier: { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
  offline_buyer: { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" },
  offline_supplier: { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" },
  default: { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" },
};
const tint = (r) => ROLE_TINT[r] || ROLE_TINT.default;
const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// Read-only label/value cell
const DataField = ({ label, value, mono, full }) => (
  <div className={`ud-field ${full ? "ud-field--full" : ""}`}>
    <div className="ud-field__label">{label}</div>
    <div className={`ud-field__value ${mono ? "ud-mono" : ""}`}>
      {value || "N/A"}
    </div>
  </div>
);

const SupplierShow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [uuid, setUuid] = useState();
  const [stampid, setStampid] = useState();
  const [checksum, setChecksum] = useState();
  const [signing, setSigning] = useState(false); // Execute Contract in progress
  const [downloading, setDownloading] = useState(false); // Download Contract in progress
  const [hasContract, setHasContract] = useState(false); // Download Contract only shows once one exists

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  // Download Contract should only ever be offered once a contract has
  // actually been executed — check on load instead of only discovering
  // "there's nothing to download" after the admin clicks it.
  useEffect(() => {
    if (!currentUser?.user_id) return;
    axios
      .get(`${process.env.REACT_APP_URL}/admin/true_copy`, {
        params: { user_id: currentUser.user_id, limit: 1 },
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      })
      .then((res) => {
        setHasContract(Boolean(res.data?.data?.trueCopies?.[0]?.uuid));
      })
      .catch(() => {
        // Silent — this only gates a button's visibility, not a page load.
      });
  }, [currentUser?.user_id]);

  useEffect(() => {
    if (!location?.state) {
      navigate("/admin/dashboard");
    } else {
      const state = location.state;
      setUser({
        id: state?.id,
        name: state?.name,
        address: state?.addresses?.[0]?.address || state?.address,
        company_name: state?.company_name,
        website: state?.website,
        logo_image: state?.logo_image,
        state: state?.addresses?.[0]?.state || state?.state,
        city: state?.addresses?.[0]?.city || state?.city,
        country: state?.addresses?.[0]?.country || state?.country,
        email: state?.email,
        pincode: state?.addresses?.[0]?.pincode || state?.pincode,
        mobile_number: state?.phone,
        gst_number: state?.gst_number,
        status: state?.status,
        created_at: state?.created_at,
        user_type: state?.user_type,
        machine_configuration: state?.machine_configuration,
        business_type: state?.business_type,
        supplier_type: state?.supplier_type || state?.item?.supplier_type,
        // bank_details: state?.bank_details,
        account_holder_name: state?.account_holder_name || "",
        bank_name: state?.bank_name || "",
        branch_name: state?.branch_name || "",
        account_number: state?.account_number || "",
        ifsc_code: state?.ifsc_code || "",
      });
    }
  }, [location, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Returns { signedWith } on success, or throws with a user-readable message.
  // (Previously fired its own toasts and could end silently when every stamp
  // was already used — the caller now owns all feedback.)
  const getEstampid = async (cs, ts, uuid, currentUser) => {
    const formData = new FormData();
    formData.append("timestamp", ts);
    formData.append("checksum", cs);
    formData.append("initiator", "helpdesk+qamypackgenieapiuser@truecopy.com");

    const res = await axios.post(
      "https://qamypackgenie.truecopy.in/api/gen/v3/getestamplist",
      formData,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      }
    );

    if (!res.data || res.data.length === 0) {
      throw new Error("No e-stamps are available to sign this contract.");
    }

    // Try each available stamp until one signs successfully.
    for (const stamp of res.data) {
      try {
        setStampid(stamp.stampid);

        const csFinal = await generateChecksum(
          process.env.REACT_APP_TRUE_COPY_API_KEY,
          uuid
        );

        await signdocument(csFinal, uuid, stamp.stampid, currentUser);
        return { signedWith: stamp.stampid }; // success
      } catch (err) {
        const msg =
          err.response?.data?.message || err.message || "Unknown error";

        if (
          msg.includes("Estamp invalid / used") ||
          err.response?.data?.errCode === 69
        ) {
          console.warn("Stamp already used, trying next:", stamp.stampid);
          continue;
        }
        throw err;
      }
    }

    // Every stamp was already used — this used to fall through with no message.
    throw new Error(
      "All available e-stamps have already been used. Please add a new e-stamp and try again."
    );
  };

  const signdocument = async (cs, uuid, stampid, currentUser) => {
    const signer = [
      {
        signer: "dev1.webglowx@gmail.com", // `${currentUser.email}`
        signer_name: `${currentUser.name}`,
        signloc: "1[42:36]",
        seq_no: 1,
        parallel_index: 1,
        signer_mobile: "",
        signtype: "AADHAAR_SIGN",
      },
      {
        signer: "dev2.webglowx@gmail.com", // `${user.email}`
        signer_name: `${user.name}`,
        signloc: "1[399:46]",
        seq_no: 2,
        parallel_index: 1,
        signer_mobile: "",
        signtype: "AADHAAR_SIGN",
      },
    ];

    const formData = new FormData();
    const response = await fetch("/assets/img/sample.pdf");
    const blob = await response.blob();

    formData.append("uuid", uuid);
    formData.append("cs", cs);
    formData.append("initiator", "helpdesk+qamypackgenieapiuser@truecopy.com");
    formData.append("workflow_id", process.env.REACT_APP_WORKFLOWID);
    formData.append("callbackurl", `${window.location.origin}/admin/dashboard`);
    formData.append("uploadfile", blob, "sample.pdf");
    formData.append("signerlist", JSON.stringify(signer));
    formData.append("estampid", stampid);

    const res = await axios.post(
      "https://qamypackgenie.truecopy.in/services/corpservice/v2/uploadfile",
      formData,
      { headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` } }
    );

    if (res.status !== 200) {
      throw new Error("Upload failed or status not 200");
    }

    return res.data;
  };

  const generateChecksum = async (apiKey, uuid) => {
    if (!apiKey || !uuid) throw new Error("API key or UUID missing");

    const data = new TextEncoder().encode(apiKey + uuid);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  // Single entry point for the "Execute Contract" button. It drives all of the
  // on-screen feedback: a loading toast the moment it is clicked, then exactly
  // one definite outcome (success or a specific error). The button previously
  // ran a multi-second, multi-API flow with no indication anything was
  // happening, and several outcomes produced no message at all.
  const executeContract = async () => {
    if (signing) return; // guard: avoid duplicate signings / duplicate emails
    if (!currentUser?.user_id) {
      toast.error("Your session has expired. Please sign in again.");
      return;
    }

    setSigning(true);
    const toastId = toast.loading("Executing contract, please wait…");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL}/admin/true_copy`,
        { user_id: currentUser.user_id },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      if (!res.data?.success || !res.data?.data?.uuid) {
        throw new Error(
          res.data?.message || "Could not start the contract workflow."
        );
      }

      const uuid = res.data.data.uuid;
      setUuid(uuid);

      const ts = moment().format("DDMMYYYYHH:mm:ss");
      const cs = CryptoJS.SHA256(
        process.env.REACT_APP_TRUE_COPY_API_KEY + ts
      ).toString(CryptoJS.enc.Hex);

      const { signedWith } = await getEstampid(cs, ts, uuid, currentUser);

      setHasContract(true); // reveal Download Contract without a page refresh

      toast.success(
        `Contract executed successfully. Signing request (e-stamp ${signedWith}) has been sent by email.`,
        { id: toastId, duration: 6000 }
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while executing the contract.";
      console.error("Execute contract failed:", err);
      toast.error(`Contract execution failed: ${msg}`, {
        id: toastId,
        duration: 6000,
      });
    } finally {
      setSigning(false);
    }
  };

  // Looks up whether a contract has actually been executed for this user
  // (createTrueCopy stores one row per Execute Contract run), then fetches
  // the signed PDF from TrueCopy's own gateway (GET .../fetchsignedfile/
  // {uuid}/{cs}, per TrueCopy's "Download Signed File" API — same checksum
  // scheme, sha256(APIKEY+uuid), that signdocument/generateChecksum above
  // already use for uploading) and saves it locally.
  const downloadContract = async () => {
    if (downloading) return;
    if (!currentUser?.user_id) {
      toast.error("Your session has expired. Please sign in again.");
      return;
    }

    setDownloading(true);
    const toastId = toast.loading("Fetching signed contract…");
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL}/admin/true_copy`, {
        params: { user_id: currentUser.user_id, limit: 1 },
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      });

      const record = res.data?.data?.trueCopies?.[0];
      if (!record?.uuid) {
        toast.error('No executed contract found yet — run "Execute Contract" first.', {
          id: toastId,
        });
        return;
      }

      const cs = await generateChecksum(
        process.env.REACT_APP_TRUE_COPY_API_KEY,
        record.uuid
      );

      const fileRes = await axios.get(
        `https://qamypackgenie.truecopy.in/services/corpservice/v2/fetchsignedfile/${record.uuid}/${cs}`,
        { responseType: "blob" }
      );

      const blobUrl = URL.createObjectURL(fileRes.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `contract-${record.uuid}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success("Contract downloaded.", { id: toastId });
    } catch (err) {
      // fetchsignedfile 500s both on a genuine error and when the document
      // simply isn't ready yet (a signer hasn't completed their signature)
      // — TrueCopy's own error body (JSON) still comes back as a Blob here
      // because of responseType:"blob", so it has to be read back out.
      const fallbackMsg =
        "The signed document isn't ready yet — both signers need to complete Aadhaar e-sign first.";
      let msg = fallbackMsg;
      try {
        let raw;
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          raw = JSON.parse(text)?.message;
        } else if (err?.response?.data?.message) {
          raw = err.response.data.message;
        } else if (err?.message) {
          raw = err.message;
        }
        // TrueCopy's gateway 500s with the same raw internal exception text
        // (e.g. "Cannot invoke ...Sreq.getStatus() ... is null") whenever the
        // document hasn't finished being signed yet — that's not something an
        // admin can act on, so keep the friendly fallback for it instead.
        if (raw && !/cannot invoke|nullpointer|\bcom\.\w+\./i.test(raw)) {
          msg = raw;
        }
      } catch {
        // Body wasn't readable/JSON — keep the generic message above.
      }
      toast.error(msg, { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const statusActive = user?.status === true || user?.status === "active";
  const statusInactive = user?.status === false || user?.status === "inactive";
  const t = tint(user?.user_type);
  const isSupplier = user?.user_type === "supplier";

  // Onboarding steps reflect the real account status
  const steps = [
    { label: "Registered", done: true },
    { label: "Verified", done: statusActive, current: !statusActive },
    { label: "Contract", done: false, current: statusActive },
    { label: "Live", done: false },
  ];

  return (
    <div className="ud">
      {/* Page header */}
      <div className="ud-head">
        <div>
          <h1 className="ud-title">User Details</h1>
          <p className="ud-sub">
            Registration profile, business information and onboarding status.
          </p>
        </div>
        <div className="ud-head__actions">
          <button type="button" className="ud-btn" onClick={() => navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <button
            type="button"
            className="ud-btn ud-btn--primary"
            onClick={() =>
              navigate("/admin/edit-user", { state: { item: user } })
            }
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
            </svg>
            Edit
          </button>
        </div>
      </div>

      {/* Identity banner */}
      <div className="ud-banner">
        <div className="ud-banner__avatar">{initialsOf(user?.name)}</div>
        <div style={{ minWidth: 0 }}>
          <div className="ud-banner__row">
            <h2 className="ud-banner__name">{user?.name || "N/A"}</h2>
            {user?.user_type && (
              <span className="ud-pill" style={{ background: t.bg, color: t.color }}>
                {(user.user_type || "").replaceAll("_", " ")}
              </span>
            )}
            {statusActive && (
              <span className="ud-pill ud-pill--ok">
                <span className="ud-dot" />Active
              </span>
            )}
            {statusInactive && (
              <span className="ud-pill ud-pill--off">Inactive</span>
            )}
          </div>
          <p className="ud-banner__meta">
            {user?.email || "N/A"} &nbsp;·&nbsp;
            <span className="ud-mono">{user?.mobile_number || "N/A"}</span>
            {user?.created_at &&
              ` · Joined ${moment(user.created_at).format("DD MMM YYYY")}`}
          </p>
        </div>
        <button type="button" className="ud-sign" onClick={executeContract} disabled={signing}
          style={{ opacity: signing ? 0.7 : 1, cursor: signing ? "wait" : "pointer" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
          </svg>
          {signing ? "Executing…" : "Execute Contract (Sign)"}
        </button>
        {hasContract && (
          <button type="button" className="ud-btn" onClick={downloadContract} disabled={downloading}
            style={{ opacity: downloading ? 0.7 : 1, cursor: downloading ? "wait" : "pointer" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {downloading ? "Checking…" : "Download Contract"}
          </button>
        )}
      </div>

      {/* Onboarding status strip */}
      <div className="ud-card ud-steps-card">
        <div className="ud-steps-cap">Onboarding Status</div>
        <div className="ud-steps">
          {steps.map((s, i) => (
            <div className="ud-step-wrap" key={s.label}>
              <div className="ud-step">
                <div
                  className={`ud-step__dot ${
                    s.done ? "is-done" : s.current ? "is-current" : ""
                  }`}
                >
                  {s.done ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                <span
                  className={`ud-step__label ${
                    s.done || s.current ? "is-strong" : ""
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <span
                  className={`ud-step__bar ${s.done ? "is-done" : ""}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact + Business */}
      <div className="ud-grid2">
        <div className="ud-card">
          <div className="ud-card__head">
            <span className="ud-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </span>
            <h3 className="ud-card__title">Contact &amp; Address</h3>
          </div>
          <div className="ud-card__body ud-fields">
            <DataField label="Email" value={user?.email} />
            <DataField label="Phone" value={user?.mobile_number} mono />
            <DataField label="Address" value={user?.address} full />
            <DataField label="Country" value={user?.country} />
            <DataField label="State" value={user?.state} />
            <DataField label="City" value={user?.city} />
            <DataField label="Pincode" value={user?.pincode} />
          </div>
        </div>

        <div className="ud-card">
          <div className="ud-card__head">
            <span className="ud-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </span>
            <h3 className="ud-card__title">Business Details</h3>
          </div>
          <div className="ud-card__body ud-fields">
            <DataField label="Role" value={user?.user_type} />
            <DataField label="Company Name" value={user?.company_name} />
            <DataField label="GST Number" value={user?.gst_number} mono />
            <DataField
              label="Machine Configuration"
              value={user?.machine_configuration}
            />
            <DataField label="Business Type" value={user?.business_type} />
            <DataField label="Date & Time" value={formatDate(user?.created_at)} mono />
          </div>
        </div>
      </div>

      {/* Supplier-only sections */}
      {isSupplier && (
        <>
          <div className="ud-card" style={{ marginTop: 16 }}>
            <div className="ud-card__head">
              <span className="ud-card__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
                </svg>
              </span>
              <h3 className="ud-card__title">Company Details</h3>
            </div>
            <div className="ud-card__body ud-fields">
              <DataField label="Company Name" value={user?.company_name} />
              <DataField label="Website" value={user?.website} />
              <DataField label="GST Number" value={user?.gst_number} mono />
              <DataField label="Business Type" value={user?.business_type} />
            </div>
          </div>

          <div className="ud-card" style={{ marginTop: 16 }}>
            <div className="ud-card__head">
              <span className="ud-card__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="22" x2="21" y2="22" />
                  <line x1="6" y1="18" x2="6" y2="11" />
                  <line x1="10" y1="18" x2="10" y2="11" />
                  <line x1="14" y1="18" x2="14" y2="11" />
                  <line x1="18" y1="18" x2="18" y2="11" />
                  <polygon points="12 2 20 7 4 7" />
                </svg>
              </span>
              <h3 className="ud-card__title">Bank Details</h3>
            </div>
            <div className="ud-card__body ud-fields">
              <DataField label="Account Holder Name" value={user?.account_holder_name} />
              <DataField label="Bank Name" value={user?.bank_name} />
              <DataField label="Account Number" value={user?.account_number} mono />
              <DataField label="IFSC Code" value={user?.ifsc_code} mono />
              <DataField label="Branch Name" value={user?.branch_name} />
            </div>
          </div>

          <div className="ud-card" style={{ marginTop: 16 }}>
            <div className="ud-card__head">
              <span className="ud-card__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                </svg>
              </span>
              <h3 className="ud-card__title">Products</h3>
            </div>
            <div className="ud-card__body">
              <SupplierProducts supplierId={user?.id} />
            </div>
          </div>
        </>
      )}

      <ShowStyles />
    </div>
  );
};

function ShowStyles() {
  return (
    <style>{`
.ud{ color:var(--color-text-primary); }
.ud *{ box-sizing:border-box; }
.ud-mono{ font-family:var(--font-mono); }

.ud-head{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:18px; }
.ud-title{ margin:0; font-size:24px; font-weight:800; letter-spacing:-.025em; }
.ud-sub{ margin:5px 0 0; font-size:13.5px; color:var(--color-text-secondary); }
.ud-head__actions{ display:flex; align-items:center; gap:9px; }
.ud-btn{ display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 14px; border-radius:10px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-primary); font-family:inherit; font-size:12.5px; font-weight:700; cursor:pointer; transition:background .15s ease; }
.ud-btn:hover{ background:var(--color-hover); }
.ud-btn--primary{ border:0; color:#fff; background:linear-gradient(135deg,var(--color-primary),var(--color-primary-2)); box-shadow:0 2px 8px rgba(79,70,229,.3); }
.ud-btn--primary:hover{ filter:brightness(1.06); background:linear-gradient(135deg,var(--color-primary),var(--color-primary-2)); }

.ud-card{ background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card); overflow:hidden; }
.ud-card__head{ display:flex; align-items:center; gap:10px; padding:15px 20px; border-bottom:1px solid var(--color-border-soft); }
.ud-card__icon{ width:30px; height:30px; border-radius:8px; background:var(--color-primary-weak); color:var(--color-primary); display:flex; align-items:center; justify-content:center; flex:0 0 30px; }
.ud-card__title{ margin:0; font-size:14.5px; font-weight:700; }
.ud-card__body{ padding:20px; }

/* identity banner */
.ud-banner{ background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card); padding:20px 22px; margin-bottom:16px; display:flex; align-items:center; gap:18px; flex-wrap:wrap; }
.ud-banner__avatar{ width:60px; height:60px; border-radius:16px; background:linear-gradient(135deg,#7C3AED,#6366F1); color:#fff; display:flex; align-items:center; justify-content:center; font-size:22px; font-weight:800; flex:0 0 60px; box-shadow:0 4px 14px rgba(99,102,241,.35); }
.ud-banner__row{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ud-banner__name{ margin:0; font-size:20px; font-weight:800; letter-spacing:-.02em; }
.ud-banner__meta{ margin:6px 0 0; font-size:12.5px; color:var(--color-text-secondary); }
.ud-pill{ display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:700; border-radius:6px; padding:3px 9px; text-transform:capitalize; }
.ud-pill--ok{ color:var(--color-success-text); background:var(--color-success-bg); }
.ud-pill--off{ color:var(--color-text-secondary); background:var(--color-surface-2); }
.ud-dot{ width:6px; height:6px; border-radius:50%; background:currentColor; }
.ud-sign{ margin-left:auto; display:flex; align-items:center; gap:8px; height:42px; padding:0 18px; border-radius:11px; border:0; background:linear-gradient(135deg,#F59E0B,#D97706); color:#fff; font-family:inherit; font-size:13px; font-weight:700; cursor:pointer; box-shadow:0 3px 12px rgba(217,119,6,.32); white-space:nowrap; }
.ud-sign:hover{ filter:brightness(1.05); }

/* steps */
.ud-steps-card{ padding:18px 22px; margin-bottom:16px; }
.ud-steps-cap{ font-size:10.5px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:var(--color-text-tertiary); margin-bottom:14px; }
.ud-steps{ display:flex; align-items:flex-start; }
.ud-step-wrap{ display:flex; align-items:center; flex:1; }
.ud-step-wrap:last-child{ flex:0 0 auto; }
.ud-step{ display:flex; flex-direction:column; align-items:center; gap:7px; flex:0 0 auto; }
.ud-step__dot{ width:28px; height:28px; border-radius:50%; background:var(--color-surface-2); color:var(--color-text-tertiary); border:2px solid var(--color-border); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:800; }
.ud-step__dot.is-done{ background:var(--color-success-text); border-color:var(--color-success-text); color:#fff; }
.ud-step__dot.is-current{ background:var(--color-warning-bg); border-color:var(--color-warning-text); color:var(--color-warning-text); }
.ud-step__label{ font-size:11.5px; font-weight:600; color:var(--color-text-tertiary); }
.ud-step__label.is-strong{ font-weight:700; color:var(--color-text-primary); }
.ud-step__bar{ flex:1; height:2px; background:var(--color-border); margin:0 8px 22px; border-radius:2px; }
.ud-step__bar.is-done{ background:var(--color-success-text); }

/* fields */
.ud-grid2{ display:grid; grid-template-columns:1fr; gap:16px; }
@media (min-width:900px){ .ud-grid2{ grid-template-columns:1fr 1fr; } }
.ud-fields{ display:grid; grid-template-columns:1fr 1fr; gap:20px 24px; }
.ud-field--full{ grid-column:1 / -1; }
.ud-field__label{ font-size:10.5px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:var(--color-text-tertiary); margin-bottom:5px; }
.ud-field__value{ font-size:13.5px; font-weight:600; color:var(--color-text-primary); word-break:break-word; }
@media (max-width:520px){ .ud-fields{ grid-template-columns:1fr; } }
    `}</style>
  );
}

export default SupplierShow;
